import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, CheckCircle2, Calendar } from "lucide-react";
import Seo from "@/components/site/Seo";
import JsonLd from "@/components/site/JsonLd";
import BookingEmbed from "@/components/site/BookingEmbed";
import { getAttribution, trackEvent } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || "";

const HELP_OPTIONS = [
  "Marketo support",
  "Campaign operations",
  "Salesforce / CRM alignment",
  "Lifecycle / lead routing",
  "Reporting and attribution",
  "Managed marketing operations support",
  "Not sure yet",
];

const TIMELINE_OPTIONS = [
  "As soon as possible",
  "This month",
  "Next 1–3 months",
  "Exploring options",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    help_with: "",
    timeline: "",
    message: "",
    consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [attribution, setAttribution] = useState({ original: {}, latest: {} });

  useEffect(() => {
    setAttribution(getAttribution());
    trackEvent("form_start", { form: "contact" });

    // Preselect the Help-with dropdown when arriving from a Featured Platforms CTA.
    try {
      const params = new URLSearchParams(window.location.search);
      const focus = (params.get("focus") || "").toLowerCase();
      const map = {
        marketo: "Marketo support",
        salesforce: "Salesforce / CRM alignment",
        "campaign-ops": "Campaign operations",
        managed: "Managed marketing operations support",
        martech: "Reporting and attribution",
        reporting: "Reporting and attribution",
      };
      const preset = map[focus];
      if (preset) {
        setForm((f) => ({ ...f, help_with: preset }));
        trackEvent("form_preset_focus", { focus, help_with: preset });
        // Smooth scroll to the form on the next tick so the user lands where they need to be.
        setTimeout(() => {
          document
            .querySelector("[data-testid='contact-form']")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 250);
      }
    } catch {
      // ignore — defensive only
    }
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent to continue.");
      return;
    }
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      email: form.email,
      company: form.company || null,
      service: form.help_with || null,
      message: form.message || null,
      help_with: form.help_with ? [form.help_with] : [],
      timeline: form.timeline || null,
      consent: form.consent,
      attribution,
      page_url: typeof window !== "undefined" ? window.location.href : null,
    };
    try {
      await axios.post(`${API}/contact`, payload);
      trackEvent("form_submit", { form: "contact", help_with: form.help_with });
      toast.success("Thanks — we'll reply within one business day.");
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        Array.isArray(detail) ? detail[0]?.msg || "Submission failed." : "Submission failed. Try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Xadentra | Marketing Automation & Marketo Support"
        description="Contact Xadentra for Marketo Engage, campaign operations, CRM alignment, reporting, and B2B marketing automation support."
        path="/contact"
      />
      <JsonLd
        id="contact-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://xadentra.com/" },
            { "@type": "ListItem", position: 2, name: "Contact", item: "https://xadentra.com/contact" },
          ],
        }}
      />
      <JsonLd
        id="contact-page"
        data={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Xadentra",
          url: "https://xadentra.com/contact",
          mainEntity: {
            "@type": "Organization",
            name: "Xadentra",
            email: "contact" + "@" + "xadentra.com",
            url: "https://xadentra.com/",
          },
        }}
      />

      {/* HERO */}
      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 460, height: 460, background: "#6D4DFF", top: -160, left: -100, opacity: 0.55 }} />
        <div className="xa-blob" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -120, right: -100, opacity: 0.3 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">Contact</div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[0.98] max-w-4xl">
            Tell us what you&apos;re trying to fix.
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">
            A few short lines on your current setup is enough. We&apos;ll review your request
            and suggest the most practical next step — usually a 30-minute working session
            with a senior Marketo or Salesforce specialist.
          </p>
          <p className="mt-4 text-sm text-white/55">
            Response within <span className="text-white">one business day</span>.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            {BOOKING_URL && (
              <a
                href="#booking-section"
                data-testid="contact-hero-book-meeting"
                onClick={(e) => {
                  e.preventDefault();
                  trackEvent("book_call_click", { id: "contact_hero_booking_inpage" });
                  document.getElementById("booking-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="xa-btn xa-btn-primary"
              >
                <Calendar className="w-4 h-4" /> Book a meeting <ArrowUpRight className="w-4 h-4" />
              </a>
            )}
            <a
              href="#contact-form"
              data-testid="contact-hero-send-message"
              className={BOOKING_URL ? "xa-btn xa-btn-ghost-dark" : "xa-btn xa-btn-primary"}
            >
              {BOOKING_URL ? "Or send us a message" : "Send us a message"}
              {!BOOKING_URL && <ArrowUpRight className="w-4 h-4" />}
            </a>
          </div>
        </div>
      </section>

      {/* FORM + ASIDE */}
      <section className="bg-[#F7F9FC] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Aside */}
          <aside className="lg:col-span-4 space-y-8">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">Direct</div>
              <a
                href={"mail" + "to:" + "contact" + "@" + "xadentra.com"}
                data-testid="contact-direct-email"
                className="mt-3 inline-flex items-center gap-3 text-xl font-display font-semibold text-[#07111F] hover:text-[#6D4DFF]"
              >
                <Mail className="w-5 h-5" /> {"contact" + "@" + "xadentra.com"}
              </a>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">Where we work from</div>
              <div className="mt-4 flex items-start gap-3 text-sm text-[#07111F]">
                <MapPin className="w-5 h-5 mt-0.5 text-[#6D4DFF] shrink-0" />
                <div>
                  <div className="font-display text-base font-semibold text-[#07111F]">Xadentra Inc.</div>
                  <div className="mt-1 text-xs font-mono uppercase tracking-[0.18em] text-[#6B7280]">Remote-first</div>
                  <ul className="mt-4 space-y-2" data-testid="contact-regions">
                    {["EMEA", "APAC", "North America"].map((r) => (
                      <li key={r} className="flex items-center gap-2.5 text-sm text-[#07111F]">
                        <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[#6D4DFF] to-[#00D4FF] shrink-0" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 relative overflow-hidden">
              <div className="flex items-start gap-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_xadentra-pro/artifacts/sa9jya8h_20240528_marci_25329_r2.webp"
                  alt="Márton Dávid Iványi, Founder and CEO of Xadentra"
                  width={144}
                  height={180}
                  loading="lazy"
                  className="w-20 h-24 rounded-xl object-cover flex-shrink-0 border border-[#E5E7EB]"
                  data-testid="contact-founder-photo"
                />
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
                    You&apos;ll hear back from a real operator
                  </div>
                  <div className="mt-2 font-semibold text-[#07111F]">Márton Dávid Iványi</div>
                  <div className="text-xs text-[#5B6B80]">
                    Founder & CEO · 14+ yrs Unilever
                  </div>
                  <a
                    href="https://www.linkedin.com/in/marton-david-ivanyi/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-founder-linkedin"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#6D4DFF] hover:text-[#07111F] transition-colors"
                  >
                    LinkedIn <ArrowUpRight className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-[#07111F] text-white border border-[#6D4DFF]/30 rounded-2xl p-7 relative overflow-hidden">
              <div className="xa-blob pointer-events-none" style={{ width: 220, height: 220, background: "#6D4DFF", top: -90, right: -80, opacity: 0.35 }} />
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#3EE8A5]">What happens next</div>
                <ol className="mt-5 space-y-4 text-sm text-white/80">
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-[#00D4FF] mt-0.5">01</span>
                    <span>A senior strategist reviews your note.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-[#00D4FF] mt-0.5">02</span>
                    <span>We reply within one business day with a 30-min working session slot.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-[#00D4FF] mt-0.5">03</span>
                    <span>If we&apos;re the wrong fit, we&apos;ll tell you — and point you to someone who is.</span>
                  </li>
                </ol>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8" id="contact-form">
            {done ? (
              <div data-testid="contact-success" className="bg-white border border-[#3EE8A5]/40 rounded-2xl p-8 sm:p-10">
                <CheckCircle2 className="w-10 h-10 text-[#3EE8A5]" />
                <h2 className="font-display mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-[#07111F]">
                  Thanks — we&apos;ve got your request.
                </h2>
                <p className="mt-3 text-[#6B7280] leading-relaxed">
                  A senior specialist will review it and reply within one business day with the
                  most practical next step. In the meantime, feel free to browse our playbooks or
                  send us another note.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/resources" className="xa-btn xa-btn-ghost-light">Read insights</Link>
                  <Link to="/case-studies" className="xa-btn xa-btn-ghost-light">View case studies</Link>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="contact-send-another"
                    className="xa-btn xa-btn-primary"
                  >
                    Send another message
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="bg-white border border-[#E5E7EB] rounded-2xl p-7 sm:p-9 lg:p-10 space-y-5"
                data-testid="contact-form"
              >
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
                    Send us a quick brief
                  </div>
                  <h2 className="font-display mt-3 text-2xl font-semibold text-[#07111F]">
                    Tell us what you are trying to fix.
                  </h2>
                  <p className="mt-3 text-sm text-[#6B7280] leading-relaxed">
                    We&apos;ll review your request and suggest the most practical next step.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Name" required>
                    <input
                      data-testid="contact-name"
                      required
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      className="xa-field"
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="Work email" required>
                    <input
                      data-testid="contact-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      className="xa-field"
                      autoComplete="email"
                    />
                  </Field>
                  <Field label="Company">
                    <input
                      data-testid="contact-company"
                      value={form.company}
                      onChange={(e) => set("company", e.target.value)}
                      className="xa-field"
                      autoComplete="organization"
                    />
                  </Field>
                  <Field label="Timeline">
                    <select
                      data-testid="contact-timeline"
                      value={form.timeline}
                      onChange={(e) => set("timeline", e.target.value)}
                      className="xa-field"
                    >
                      <option value="">Select…</option>
                      {TIMELINE_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field label="What do you need help with?" required>
                  <select
                    data-testid="contact-help-with"
                    required
                    value={form.help_with}
                    onChange={(e) => set("help_with", e.target.value)}
                    className="xa-field"
                  >
                    <option value="">Select…</option>
                    {HELP_OPTIONS.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Message">
                  <textarea
                    data-testid="contact-message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    className="xa-field resize-none"
                    placeholder="Optional — a few lines on your current setup or what you'd like to change."
                  />
                </Field>

                <label className="flex items-start gap-3 cursor-pointer text-sm text-[#07111F]">
                  <input
                    type="checkbox"
                    data-testid="contact-consent"
                    checked={form.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                    className="mt-1 accent-[#6D4DFF]"
                    required
                  />
                  <span>
                    I agree to be contacted by Xadentra about my inquiry and understand that my
                    information will be handled according to the{" "}
                    <Link to="/privacy-policy" className="text-[#6D4DFF] underline">Privacy Policy</Link>.
                  </span>
                </label>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
                  <button
                    disabled={busy}
                    type="submit"
                    data-testid="contact-submit"
                    className="xa-btn xa-btn-primary disabled:opacity-60"
                  >
                    {busy ? "Submitting…" : "Send"} <ArrowUpRight className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-[#6B7280]">
                    Response within one business day.
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* BOOKING EMBED */}
      <section id="booking-section" className="bg-[#07111F] text-white border-b border-white/10 relative overflow-hidden scroll-mt-20">
        <div className="xa-blob pointer-events-none" style={{ width: 520, height: 520, background: "#6D4DFF", top: -200, right: -160, opacity: 0.35 }} />
        <div className="xa-blob pointer-events-none" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -140, left: -100, opacity: 0.22 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-40 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 relative">
          {/* Mobile-only: header copy ABOVE the booking iframe */}
          <div className="lg:hidden">
            <BookingHeader />
          </div>

          {/* Booking iframe — first on mobile (after header), right column on desktop */}
          <div className="lg:col-span-7 lg:order-2">
            <BookingEmbed />
            <p className="mt-4 text-[11px] font-mono uppercase tracking-[0.18em] text-white/40 text-center">
              Powered by Google Workspace Calendar · Times shown in your local timezone
            </p>
          </div>

          {/* Mobile-only: agenda + strategist + stats BELOW the booking iframe */}
          <div className="lg:hidden">
            <BookingDetails />
          </div>

          {/* Desktop-only: full sticky aside on the left */}
          <div className="hidden lg:block lg:col-span-5 lg:order-1 lg:sticky lg:top-24 h-fit">
            <BookingHeader />
            <BookingDetails />
          </div>
        </div>
      </section>
    </>
  );
}

function BookingHeader() {
  return (
    <>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">
        Pick a time directly
      </div>
      <h2 className="font-display mt-5 text-4xl lg:text-5xl font-bold tracking-tight leading-[1.04]">
        Book a working<br />session.
      </h2>
      <p className="mt-6 text-white/70 leading-relaxed text-base lg:text-lg max-w-md">
        Pick a time directly on a senior specialist&apos;s calendar — a working session
        focused on your setup and next best steps.
      </p>
    </>
  );
}

function BookingDetails() {
  return (
    <>
      {/* Agenda chips */}
      <div className="mt-9">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 mb-3">
          On the call
        </div>
        <div className="flex flex-wrap gap-2" data-testid="booking-agenda">
          {[
            "Your current Adobe Marketo Engage / SFDC setup",
            "What's slowing the team down",
            "Where Xadentra can (and can't) help",
            "Honest first take",
          ].map((t) => (
            <span
              key={t}
              className="text-[12px] px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/12 text-white/80 hover:border-[#6D4DFF]/50 hover:bg-white/[0.09] transition"
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Host signature */}
      <div className="mt-9 flex items-start gap-4 p-5 rounded-xl bg-white/[0.04] border border-white/10">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#6D4DFF] to-[#00D4FF] grid place-items-center text-white font-display font-bold text-base shadow-[0_8px_20px_-6px_rgba(109,77,255,0.6)]">
          XA
        </div>
        <div>
          <div className="font-display font-semibold text-white text-sm">Xadentra strategy team</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55 mt-1">
            Senior Adobe Marketo Engage · Salesforce · MOPs
          </div>
          <div className="mt-3 text-xs text-white/65 leading-relaxed">
            You&apos;ll speak directly with a senior marketing operations specialist.
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-3 gap-4">
        {[
          { v: "30 min", l: "Focused" },
          { v: "1 biz day", l: "Confirmation" },
          { v: "0", l: "Pitch decks" },
        ].map((s, i) => (
          <div key={i}>
            <div className="font-display text-xl font-bold xa-gradient-text">{s.v}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/50 mt-1">
              {s.l}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="xa-field-label">
        {label}
        {required && <span className="text-[#6D4DFF] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
